// Admin returns management — list, approve, and reject return requests
// Uses @cieda/types Return shape and PATCH /api/admin/returns/[id]/status

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  RotateCcw,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { supabase } from "@/lib/supabase";
import { formatFCFA } from "@cieda/utils";
import type {
  Return,
  ReturnStatus,
  UpdateReturnStatusPayload,
  ReturnRefundMethod,
} from "@cieda/types";

const API_URL = import.meta.env.VITE_API_URL || "";
const PAGE_SIZE = 20;

// ── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("fr-FR");
  } catch {
    return iso;
  }
}

// Status labels for the shared ReturnStatus type (lowercase)
const STATUS_LABELS: Record<ReturnStatus, string> = {
  requested: "Demandé",
  approved: "Approuvé",
  rejected: "Refusé",
  refund_pending: "Remboursement en cours",
  refunded: "Remboursé",
  closed: "Clôturé",
};

const STATUS_COLORS: Record<ReturnStatus, string> = {
  requested: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  refund_pending: "bg-blue-100 text-blue-800",
  refunded: "bg-green-100 text-green-800",
  closed: "bg-stone-100 text-stone-600",
};

const REFUND_METHOD_LABELS: Record<ReturnRefundMethod, string> = {
  wave: "Wave",
  orange_money: "Orange Money",
  cash: "Espèces",
};

// ── API calls ─────────────────────────────────────────────────────────────

async function fetchReturns(
  token: string | undefined,
  page: number,
  statusFilter: string,
): Promise<{ returns: Return[]; total: number }> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
  });
  if (statusFilter) params.set("status", statusFilter);

  const res = await fetch(`${API_URL}/api/admin/returns?${params}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return {
    returns: data?.returns ?? [],
    total: data?.pagination?.total ?? 0,
  };
}

async function updateReturnStatus(
  token: string | undefined,
  returnId: string,
  payload: UpdateReturnStatusPayload,
): Promise<Return> {
  const res = await fetch(`${API_URL}/api/admin/returns/${returnId}/status`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData?.error ?? `HTTP ${res.status}`);
  }

  const data = await res.json();
  return data?.return ?? data;
}

// ── Review modal ──────────────────────────────────────────────────────────

interface ReviewModalProps {
  readonly ret: Return;
  readonly onClose: () => void;
  readonly onUpdated: (updated: Return) => void;
}

function ReviewModal({ ret, onClose, onUpdated }: ReviewModalProps) {
  const { t } = useTranslation("admin");

  const [action, setAction] = useState<"approve" | "reject" | null>(null);
  const [refundAmount, setRefundAmount] = useState<string>(
    ret.refund_amount != null ? String(ret.refund_amount) : "",
  );
  const [refundMethod, setRefundMethod] = useState<ReturnRefundMethod>("wave");
  const [adminNotes, setAdminNotes] = useState(ret.admin_notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave =
    action !== null &&
    (action === "reject" || (refundAmount !== "" && !isNaN(Number(refundAmount)) && Number(refundAmount) >= 0));

  const handleSave = useCallback(async () => {
    if (!canSave || !action) return;
    setSaving(true);
    setError(null);

    try {
      // Use getUser() for identity verification; getSession() only for the Bearer token
      await supabase.auth.getUser();
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      let newStatus: ReturnStatus;
      let payload: UpdateReturnStatusPayload;

      if (action === "approve") {
        newStatus = "approved";
        payload = {
          status: newStatus,
          admin_notes: adminNotes.trim() || undefined,
          refund_amount: Number(refundAmount),
          refund_method: refundMethod,
        };
      } else {
        newStatus = "rejected";
        payload = {
          status: newStatus,
          admin_notes: adminNotes.trim() || undefined,
        };
      }

      const updated = await updateReturnStatus(token, ret.id, payload);
      onUpdated(updated);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("returns.error_generic"));
    } finally {
      setSaving(false);
    }
  }, [canSave, action, adminNotes, refundAmount, refundMethod, ret.id, onUpdated, onClose, t]);

  return (
    /* Modal backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-stone-900">
            {t("returns.modal_title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 transition"
            aria-label={t("actions.close")}
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        {/* Return summary */}
        <div className="rounded-lg bg-stone-50 border border-stone-100 p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-stone-500">{t("returns.col_order")}</span>
            <span className="font-medium text-stone-900 font-mono text-xs">{ret.order_id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500">{t("returns.col_date")}</span>
            <span className="text-stone-700">{formatDate(ret.created_at)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500">{t("returns.col_status")}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ret.status]}`}>
              {STATUS_LABELS[ret.status]}
            </span>
          </div>
          <div>
            <span className="text-stone-500">{t("returns.col_reason")}</span>
            <p className="mt-1 text-stone-800 text-xs leading-relaxed">{ret.reason}</p>
          </div>
        </div>

        {/* Action choice */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setAction("approve")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-semibold transition ${
              action === "approve"
                ? "bg-green-600 border-green-600 text-white"
                : "border-stone-200 text-stone-700 hover:bg-stone-50"
            }`}
          >
            <CheckCircle2 className="h-4 w-4" />
            {t("returns.action_approve")}
          </button>
          <button
            type="button"
            onClick={() => setAction("reject")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-semibold transition ${
              action === "reject"
                ? "bg-red-600 border-red-600 text-white"
                : "border-stone-200 text-stone-700 hover:bg-stone-50"
            }`}
          >
            <XCircle className="h-4 w-4" />
            {t("returns.action_reject")}
          </button>
        </div>

        {/* Approve — refund fields */}
        {action === "approve" && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {t("returns.refund_amount_label")}
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="0"
                  className="flex-1 px-3 py-2.5 rounded-lg border border-stone-200 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <span className="text-sm text-stone-500 shrink-0">FCFA</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {t("returns.refund_method_label")}
              </label>
              <select
                value={refundMethod}
                onChange={(e) => setRefundMethod(e.target.value as ReturnRefundMethod)}
                className="w-full px-3 py-2.5 rounded-lg border border-stone-200 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                {(Object.keys(REFUND_METHOD_LABELS) as ReturnRefundMethod[]).map((m) => (
                  <option key={m} value={m}>
                    {REFUND_METHOD_LABELS[m]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Admin notes (both actions) */}
        {action !== null && (
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">
              {t("returns.admin_notes_label")}
            </label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={3}
              placeholder={t("returns.admin_notes_placeholder")}
              className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm text-stone-800 resize-none"
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Footer actions */}
        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 border border-stone-200 text-stone-600 rounded-lg hover:bg-stone-50 transition text-sm disabled:opacity-50"
          >
            {t("actions.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || saving}
            className="px-6 py-2.5 bg-secondary text-secondary-foreground font-semibold rounded-lg hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2 text-sm ml-auto"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("actions.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function ReturnsManagement() {
  const { t } = useTranslation("admin");
  useAdminPageTitle("Retours");

  const [returns, setReturns] = useState<Return[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [reviewTarget, setReviewTarget] = useState<Return | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      // Use getUser() for identity verification; getSession() only for the Bearer token
      await supabase.auth.getUser();
      const { data: { session } } = await supabase.auth.getSession();
      const { returns: data, total: count } = await fetchReturns(
        session?.access_token,
        page,
        statusFilter,
      );
      setReturns(data);
      setTotal(count);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : t("returns.error_loading"));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, t]);

  useEffect(() => { load(); }, [load]);

  const handleUpdated = useCallback((updated: Return) => {
    setReturns((prev) =>
      prev.map((r) => (r.id === updated.id ? updated : r)),
    );
  }, []);

  const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
    { value: "", label: t("returns.filter_all") },
    { value: "requested", label: STATUS_LABELS.requested },
    { value: "approved", label: STATUS_LABELS.approved },
    { value: "rejected", label: STATUS_LABELS.rejected },
    { value: "refund_pending", label: STATUS_LABELS.refund_pending },
    { value: "refunded", label: STATUS_LABELS.refunded },
    { value: "closed", label: STATUS_LABELS.closed },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page heading */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="font-display text-2xl font-semibold text-stone-900">
            {t("nav.returns")}
          </h1>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-stone-200 text-sm text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Error */}
        {fetchError && !loading && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100 text-sm text-red-700">
            <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
            <p>{fetchError}</p>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 rounded-lg bg-stone-100 animate-pulse" />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !fetchError && returns.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-stone-500">
            <RotateCcw className="h-10 w-10 text-stone-300 mb-3" />
            <p className="text-sm font-medium">{t("returns.empty")}</p>
          </div>
        )}

        {/* Returns table */}
        {!loading && returns.length > 0 && (
          <div className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 text-left">
                  <th className="py-3 px-4 text-stone-500 font-medium">
                    {t("returns.col_order")}
                  </th>
                  <th className="py-3 px-4 text-stone-500 font-medium hidden sm:table-cell">
                    {t("returns.col_date")}
                  </th>
                  <th className="py-3 px-4 text-stone-500 font-medium hidden md:table-cell">
                    {t("returns.col_reason")}
                  </th>
                  <th className="py-3 px-4 text-stone-500 font-medium">
                    {t("returns.col_status")}
                  </th>
                  <th className="py-3 px-4 text-stone-500 font-medium text-right hidden sm:table-cell">
                    {t("returns.col_refund")}
                  </th>
                  <th className="py-3 px-4 text-stone-500 font-medium text-right">
                    {t("returns.col_actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {returns.map((ret) => (
                  <tr
                    key={ret.id}
                    className="border-b border-stone-50 hover:bg-amber-50/30 transition"
                  >
                    <td className="py-3.5 px-4 font-mono text-xs text-stone-700 max-w-[140px] truncate">
                      {ret.order_id}
                    </td>
                    <td className="py-3.5 px-4 text-stone-600 hidden sm:table-cell whitespace-nowrap">
                      {formatDate(ret.created_at)}
                    </td>
                    <td className="py-3.5 px-4 text-stone-500 hidden md:table-cell max-w-[200px]">
                      <span className="block truncate" title={ret.reason}>
                        {ret.reason}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[ret.status]}`}
                      >
                        {STATUS_LABELS[ret.status]}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right font-semibold text-stone-800 hidden sm:table-cell whitespace-nowrap">
                      {ret.refund_amount != null
                        ? formatFCFA(ret.refund_amount)
                        : <span className="text-stone-300 font-normal">—</span>}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      {ret.status === "requested" && (
                        <button
                          type="button"
                          onClick={() => setReviewTarget(ret)}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 transition"
                        >
                          {t("returns.action_review")}
                        </button>
                      )}
                      {ret.status !== "requested" && (
                        <button
                          type="button"
                          onClick={() => setReviewTarget(ret)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-50 transition"
                        >
                          {t("returns.action_view")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-stone-500">
              {t("returns.pagination_info", { page, total_pages: totalPages, total })}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="p-1.5 rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-50 disabled:opacity-40 transition"
                aria-label={t("returns.prev_page")}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-stone-600">{page} / {totalPages}</span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="p-1.5 rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-50 disabled:opacity-40 transition"
                aria-label={t("returns.next_page")}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Review modal */}
      {reviewTarget && (
        <ReviewModal
          ret={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onUpdated={handleUpdated}
        />
      )}
    </AdminLayout>
  );
}
